/**
 * Invoices Firestore Service
 * Direct Firestore SDK calls for invoices and payments operations
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../config/firebase';

// Helper to convert Firestore timestamp to Date
const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

// Generate invoice number: INV-YYYY-XXX
const generateInvoiceNumber = async () => {
  const year = new Date().getFullYear();
  const prefix = `INV-${year}-`;

  const invoicesRef = collection(db, 'invoices');
  const q = query(
    invoicesRef,
    where('invoiceNumber', '>=', prefix),
    where('invoiceNumber', '<', prefix + '\uf8ff'),
    orderBy('invoiceNumber', 'desc'),
    firestoreLimit(1)
  );

  const snapshot = await getDocs(q);

  let nextNumber = 1;
  if (!snapshot.empty) {
    const lastInvoiceNumber = snapshot.docs[0].data().invoiceNumber;
    const lastNumber = parseInt(lastInvoiceNumber.split('-')[2], 10);
    nextNumber = lastNumber + 1;
  }

  return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
};

// Calculate invoice status based on payments and due date
const calculateStatus = (invoice) => {
  if (invoice.status === 'cancelled') return 'cancelled';
  if (invoice.status === 'draft') return 'draft';

  const balance = invoice.totalAmount - (invoice.amountPaid || 0);

  if (balance <= 0) return 'paid';
  if (invoice.amountPaid > 0) return 'partial';

  // Check if overdue
  const dueDate = convertTimestamp(invoice.dueDate);
  if (dueDate && new Date() > dueDate) return 'overdue';

  return 'issued';
};

// Helper to convert document to invoice object
const docToInvoice = (doc, payments = []) => {
  const data = doc.data();
  const invoice = {
    id: doc.id,
    ...data,
    issueDate: convertTimestamp(data.issueDate),
    dueDate: convertTimestamp(data.dueDate),
    nextRecurringDate: convertTimestamp(data.nextRecurringDate),
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    payments
  };
  invoice.status = calculateStatus(invoice);
  return invoice;
};

/**
 * Get all invoices with filters
 */
export const getAll = async (filters = {}) => {
  try {
    const invoicesRef = collection(db, 'invoices');
    let constraints = [];

    // Apply filters (except 'overdue' which needs post-filter)
    if (filters.status && filters.status !== 'overdue') {
      constraints.push(where('status', '==', filters.status));
    }

    if (filters.customerPhone) {
      constraints.push(where('customerPhone', '==', filters.customerPhone));
    }

    if (filters.startDate && filters.endDate) {
      constraints.push(where('issueDate', '>=', Timestamp.fromDate(new Date(filters.startDate))));
      constraints.push(where('issueDate', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    }

    constraints.push(orderBy('createdAt', 'desc'));

    if (filters.limit) {
      constraints.push(firestoreLimit(parseInt(filters.limit)));
    }

    const q = query(invoicesRef, ...constraints);
    const snapshot = await getDocs(q);

    let invoices = snapshot.docs.map((doc) => docToInvoice(doc));

    // Filter by overdue status if requested
    if (filters.status === 'overdue') {
      invoices = invoices.filter((inv) => inv.status === 'overdue');
    }

    return invoices;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

/**
 * Get invoice by ID with payments
 */
export const getById = async (id) => {
  try {
    const invoiceRef = doc(db, 'invoices', id);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    // Get payments sub-collection
    const paymentsRef = collection(db, 'invoices', id, 'payments');
    const paymentsQuery = query(paymentsRef, orderBy('paymentDate', 'desc'));
    const paymentsSnapshot = await getDocs(paymentsQuery);

    const payments = paymentsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      paymentDate: convertTimestamp(doc.data().paymentDate),
      createdAt: convertTimestamp(doc.data().createdAt)
    }));

    return docToInvoice(invoiceDoc, payments);
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

/**
 * Create a new invoice
 */
export const create = async (invoiceData) => {
  try {
    const invoiceNumber = await generateInvoiceNumber();

    // Calculate line items totals
    const lineItems = (invoiceData.lineItems || []).map((item) => ({
      productId: item.productId || null,
      productName: item.productName?.trim() || '',
      description: item.description?.trim() || '',
      quantity: parseFloat(item.quantity) || 1,
      unitPrice: parseFloat(item.unitPrice) || 0,
      amount: (parseFloat(item.quantity) || 1) * (parseFloat(item.unitPrice) || 0)
    }));

    const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
    const taxRate = parseFloat(invoiceData.taxRate) || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount;

    const invoiceDoc = {
      invoiceNumber,
      customerPhone: invoiceData.customerPhone?.trim() || '',
      customerName: invoiceData.customerName?.trim() || '',
      customerLocation: invoiceData.customerLocation?.trim() || '',
      status: invoiceData.status || 'draft',
      issueDate: invoiceData.issueDate
        ? Timestamp.fromDate(new Date(invoiceData.issueDate))
        : null,
      dueDate: invoiceData.dueDate ? Timestamp.fromDate(new Date(invoiceData.dueDate)) : null,
      lineItems,
      subtotal,
      taxRate,
      taxAmount,
      totalAmount,
      amountPaid: 0,
      balance: totalAmount,
      notes: invoiceData.notes?.trim() || '',
      terms: invoiceData.terms?.trim() || 'Payment due within 30 days of invoice date.',
      isRecurring: invoiceData.isRecurring || false,
      recurringInterval: invoiceData.recurringInterval || null,
      nextRecurringDate: invoiceData.nextRecurringDate
        ? Timestamp.fromDate(new Date(invoiceData.nextRecurringDate))
        : null,
      sourceSaleId: invoiceData.sourceSaleId || null,
      createdBy: invoiceData.createdBy || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const invoicesRef = collection(db, 'invoices');
    const docRef = await addDoc(invoicesRef, invoiceDoc);

    return {
      id: docRef.id,
      ...invoiceDoc,
      issueDate: invoiceData.issueDate ? new Date(invoiceData.issueDate) : null,
      dueDate: invoiceData.dueDate ? new Date(invoiceData.dueDate) : null,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: calculateStatus(invoiceDoc)
    };
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

/**
 * Create invoice from an existing sale
 */
export const createFromSale = async (saleId, invoiceData = {}) => {
  try {
    const saleRef = doc(db, 'sales', saleId);
    const saleDoc = await getDoc(saleRef);

    if (!saleDoc.exists()) {
      throw new Error('Sale not found');
    }

    const sale = saleDoc.data();

    // Get product details if available
    let productName = sale.productType || 'Product';
    if (sale.productId) {
      try {
        const productRef = doc(db, 'products', sale.productId);
        const productDoc = await getDoc(productRef);
        if (productDoc.exists()) {
          productName = productDoc.data().name || productName;
        }
      } catch (err) {
        console.error('Error fetching product:', err);
      }
    }

    const saleDate = convertTimestamp(sale.date);

    // Create invoice from sale data
    const newInvoiceData = {
      customerPhone: sale.customerPhone,
      customerName: sale.customerName,
      customerLocation: sale.location,
      status: 'draft',
      dueDate: invoiceData.dueDate || null,
      lineItems: [
        {
          productId: sale.productId,
          productName,
          description: sale.notes || '',
          quantity: sale.quantity || 1,
          unitPrice: sale.amount / (sale.quantity || 1),
          amount: sale.amount
        }
      ],
      taxRate: invoiceData.taxRate || 0,
      notes:
        invoiceData.notes ||
        `Created from sale on ${saleDate?.toLocaleDateString() || 'N/A'}`,
      terms: invoiceData.terms,
      sourceSaleId: saleId,
      createdBy: invoiceData.createdBy
    };

    return await create(newInvoiceData);
  } catch (error) {
    console.error('Error creating invoice from sale:', error);
    throw error;
  }
};

/**
 * Update an invoice
 */
export const update = async (id, updateData) => {
  try {
    const invoiceRef = doc(db, 'invoices', id);
    const existingDoc = await getDoc(invoiceRef);

    if (!existingDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const existing = existingDoc.data();

    // Don't allow updates to paid or cancelled invoices (except cancellation)
    if (existing.status === 'paid' && updateData.status !== 'cancelled') {
      throw new Error('Cannot update a paid invoice');
    }

    // Calculate line items totals if provided
    let lineItems = existing.lineItems;
    let subtotal = existing.subtotal;
    let taxRate = existing.taxRate;
    let taxAmount = existing.taxAmount;
    let totalAmount = existing.totalAmount;

    if (updateData.lineItems) {
      lineItems = updateData.lineItems.map((item) => ({
        productId: item.productId || null,
        productName: item.productName?.trim() || '',
        description: item.description?.trim() || '',
        quantity: parseFloat(item.quantity) || 1,
        unitPrice: parseFloat(item.unitPrice) || 0,
        amount: (parseFloat(item.quantity) || 1) * (parseFloat(item.unitPrice) || 0)
      }));

      subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
      taxRate =
        updateData.taxRate !== undefined ? parseFloat(updateData.taxRate) : existing.taxRate;
      taxAmount = subtotal * (taxRate / 100);
      totalAmount = subtotal + taxAmount;
    } else if (updateData.taxRate !== undefined) {
      taxRate = parseFloat(updateData.taxRate);
      taxAmount = subtotal * (taxRate / 100);
      totalAmount = subtotal + taxAmount;
    }

    const balance = totalAmount - (existing.amountPaid || 0);

    const updateDocData = {
      customerPhone: updateData.customerPhone?.trim() ?? existing.customerPhone,
      customerName: updateData.customerName?.trim() ?? existing.customerName,
      customerLocation: updateData.customerLocation?.trim() ?? existing.customerLocation,
      status: updateData.status || existing.status,
      issueDate: updateData.issueDate
        ? Timestamp.fromDate(new Date(updateData.issueDate))
        : existing.issueDate,
      dueDate: updateData.dueDate
        ? Timestamp.fromDate(new Date(updateData.dueDate))
        : existing.dueDate,
      lineItems,
      subtotal,
      taxRate,
      taxAmount,
      totalAmount,
      balance,
      notes: updateData.notes?.trim() ?? existing.notes,
      terms: updateData.terms?.trim() ?? existing.terms,
      isRecurring: updateData.isRecurring ?? existing.isRecurring,
      recurringInterval: updateData.recurringInterval ?? existing.recurringInterval,
      nextRecurringDate: updateData.nextRecurringDate
        ? Timestamp.fromDate(new Date(updateData.nextRecurringDate))
        : existing.nextRecurringDate,
      updatedAt: serverTimestamp()
    };

    await updateDoc(invoiceRef, updateDocData);

    return await getById(id);
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

/**
 * Delete an invoice (only draft invoices)
 */
export const remove = async (id) => {
  try {
    const invoiceRef = doc(db, 'invoices', id);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const data = invoiceDoc.data();

    // Only allow deleting draft invoices
    if (data.status !== 'draft') {
      throw new Error('Only draft invoices can be deleted');
    }

    // Delete payments sub-collection first
    const paymentsRef = collection(db, 'invoices', id, 'payments');
    const paymentsSnapshot = await getDocs(paymentsRef);

    const batch = writeBatch(db);
    paymentsSnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    batch.delete(invoiceRef);

    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

/**
 * Issue an invoice (change from draft to issued)
 */
export const issue = async (id) => {
  try {
    const invoiceRef = doc(db, 'invoices', id);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const data = invoiceDoc.data();
    if (data.status !== 'draft') {
      throw new Error('Only draft invoices can be issued');
    }

    await updateDoc(invoiceRef, {
      status: 'issued',
      issueDate: Timestamp.now(),
      updatedAt: serverTimestamp()
    });

    return await getById(id);
  } catch (error) {
    console.error('Error issuing invoice:', error);
    throw error;
  }
};

/**
 * Cancel an invoice
 */
export const cancel = async (id) => {
  try {
    const invoiceRef = doc(db, 'invoices', id);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const data = invoiceDoc.data();
    if (data.status === 'paid') {
      throw new Error('Cannot cancel a paid invoice');
    }

    await updateDoc(invoiceRef, {
      status: 'cancelled',
      updatedAt: serverTimestamp()
    });

    return await getById(id);
  } catch (error) {
    console.error('Error cancelling invoice:', error);
    throw error;
  }
};

/**
 * Record a payment for an invoice
 */
export const recordPayment = async (invoiceId, paymentData) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const invoice = invoiceDoc.data();

    if (invoice.status === 'cancelled') {
      throw new Error('Cannot record payment for a cancelled invoice');
    }

    if (invoice.status === 'draft') {
      throw new Error('Cannot record payment for a draft invoice. Please issue it first.');
    }

    const paymentAmount = parseFloat(paymentData.amount);
    if (paymentAmount <= 0) {
      throw new Error('Payment amount must be greater than zero');
    }

    const currentBalance = invoice.totalAmount - (invoice.amountPaid || 0);
    if (paymentAmount > currentBalance) {
      throw new Error(`Payment amount exceeds balance. Maximum payment: ${currentBalance.toFixed(2)}`);
    }

    // Create payment record
    const paymentDoc = {
      amount: paymentAmount,
      paymentDate: paymentData.paymentDate
        ? Timestamp.fromDate(new Date(paymentData.paymentDate))
        : Timestamp.now(),
      paymentMethod: paymentData.paymentMethod || 'cash',
      mobileMoneyProvider: paymentData.mobileMoneyProvider || null,
      transactionReference: paymentData.transactionReference?.trim() || '',
      notes: paymentData.notes?.trim() || '',
      recordedBy: paymentData.recordedBy || '',
      createdAt: serverTimestamp()
    };

    // Update invoice totals
    const newAmountPaid = (invoice.amountPaid || 0) + paymentAmount;
    const newBalance = invoice.totalAmount - newAmountPaid;
    const newStatus = newBalance <= 0 ? 'paid' : 'partial';

    // Use batch for atomic update
    const batch = writeBatch(db);

    const paymentsRef = collection(db, 'invoices', invoiceId, 'payments');
    const paymentRef = doc(paymentsRef);
    batch.set(paymentRef, paymentDoc);

    batch.update(invoiceRef, {
      amountPaid: newAmountPaid,
      balance: newBalance,
      status: newStatus,
      updatedAt: serverTimestamp()
    });

    await batch.commit();

    return {
      payment: {
        id: paymentRef.id,
        ...paymentDoc,
        paymentDate: paymentData.paymentDate ? new Date(paymentData.paymentDate) : new Date(),
        createdAt: new Date()
      },
      invoice: await getById(invoiceId)
    };
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error;
  }
};

/**
 * Get payments for an invoice
 */
export const getPayments = async (invoiceId) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const paymentsRef = collection(db, 'invoices', invoiceId, 'payments');
    const q = query(paymentsRef, orderBy('paymentDate', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      paymentDate: convertTimestamp(doc.data().paymentDate),
      createdAt: convertTimestamp(doc.data().createdAt)
    }));
  } catch (error) {
    console.error('Error getting payments:', error);
    throw error;
  }
};

/**
 * Mark invoice as fully paid
 */
export const markAsPaid = async (invoiceId, paymentData = {}) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const invoice = invoiceDoc.data();
    const balance = invoice.totalAmount - (invoice.amountPaid || 0);

    if (balance <= 0) {
      throw new Error('Invoice is already fully paid');
    }

    // Record the remaining balance as a payment
    return await recordPayment(invoiceId, {
      amount: balance,
      paymentMethod: paymentData.paymentMethod || 'cash',
      mobileMoneyProvider: paymentData.mobileMoneyProvider,
      transactionReference: paymentData.transactionReference,
      notes: paymentData.notes || 'Marked as paid',
      recordedBy: paymentData.recordedBy
    });
  } catch (error) {
    console.error('Error marking invoice as paid:', error);
    throw error;
  }
};

/**
 * Get invoices for a customer by phone
 */
export const getByCustomer = async (customerPhone, options = {}) => {
  try {
    const invoicesRef = collection(db, 'invoices');
    let constraints = [
      where('customerPhone', '==', customerPhone),
      orderBy('createdAt', 'desc')
    ];

    if (options.limit) {
      constraints.push(firestoreLimit(parseInt(options.limit)));
    }

    const q = query(invoicesRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => docToInvoice(doc));
  } catch (error) {
    console.error('Error getting customer invoices:', error);
    throw error;
  }
};

/**
 * Get overdue invoices
 */
export const getOverdue = async () => {
  try {
    const invoicesRef = collection(db, 'invoices');
    const now = Timestamp.now();

    const q = query(
      invoicesRef,
      where('status', 'in', ['issued', 'partial']),
      where('dueDate', '<', now),
      orderBy('dueDate', 'asc')
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const invoice = docToInvoice(doc);
      invoice.status = 'overdue';
      return invoice;
    });
  } catch (error) {
    console.error('Error getting overdue invoices:', error);
    throw error;
  }
};

/**
 * Get invoice statistics
 */
export const getStats = async (filters = {}) => {
  try {
    const invoicesRef = collection(db, 'invoices');
    let constraints = [];

    if (filters.startDate && filters.endDate) {
      constraints.push(where('createdAt', '>=', Timestamp.fromDate(new Date(filters.startDate))));
      constraints.push(where('createdAt', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    }

    const q = constraints.length > 0 ? query(invoicesRef, ...constraints) : query(invoicesRef);
    const snapshot = await getDocs(q);

    const now = new Date();
    let totalInvoiced = 0;
    let totalPaid = 0;
    let totalOutstanding = 0;
    let totalOverdue = 0;
    const countByStatus = {
      draft: 0,
      issued: 0,
      partial: 0,
      paid: 0,
      overdue: 0,
      cancelled: 0
    };

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const dueDate = convertTimestamp(data.dueDate);
      const balance = data.totalAmount - (data.amountPaid || 0);

      // Determine actual status
      let status = data.status;
      if (status !== 'cancelled' && status !== 'draft' && status !== 'paid') {
        if (balance <= 0) {
          status = 'paid';
        } else if (data.amountPaid > 0) {
          status = 'partial';
        } else if (dueDate && now > dueDate) {
          status = 'overdue';
        } else {
          status = 'issued';
        }
      }

      countByStatus[status] = (countByStatus[status] || 0) + 1;

      if (status !== 'cancelled' && status !== 'draft') {
        totalInvoiced += data.totalAmount || 0;
        totalPaid += data.amountPaid || 0;

        if (status !== 'paid') {
          totalOutstanding += balance;
        }

        if (status === 'overdue') {
          totalOverdue += balance;
        }
      }
    });

    return {
      totalInvoiced,
      totalPaid,
      totalOutstanding,
      totalOverdue,
      countByStatus,
      totalCount: snapshot.docs.length
    };
  } catch (error) {
    console.error('Error getting invoice stats:', error);
    throw error;
  }
};

// Export as object for compatibility with API pattern
const invoicesService = {
  getAll,
  getById,
  create,
  createFromSale,
  update,
  delete: remove,
  issue,
  cancel,
  recordPayment,
  getPayments,
  markAsPaid,
  getByCustomer,
  getOverdue,
  getStats
};

export default invoicesService;
